import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { useAccount } from '@/hooks/useAccount';
import { amplitudeModule } from '@/modules/amplitude';
import { queryParamsModule } from '@/modules/queryParams';
import {
  OnboardingOptionItem,
  OnboardingOptionsList,
} from '@/pages/Onboarding/components/List';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { OnboardingPage } from '@/pages/Onboarding/components/Page';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { useOnboarding } from '@/pages/Onboarding/context';
import { SELF_CONFIDENCE_OPTIONS } from '@/pages/Onboarding/options/SelfConfidence/SELF_CONFIDENCE_OPTIONS.ts';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { LayoutDataKeyType, LayoutDataType } from '../../types';

export const OnboardingSelfConfidence: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Self_Confidence',
      });
    }
  }, []);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const { refreshUser } = useAccount();
  const { watch, getValues } = useFormContext<LayoutDataType>();
  const selfConfidence = watch('self_confidence');

  const handleNextStep = (): void => {
    const isUserNotConfident =
      getValues('self_confidence') !== 'no_i_feel_confident';

    const selfConfidenceOption = SELF_CONFIDENCE_OPTIONS.find(
      (option) => option.id === selfConfidence,
    );

    const { name } = getValues();

    userService
      .createOrUpdate({
        name,
        onboarding_data: {
          self_confidence: selfConfidenceOption?.title ?? '',
        },
      })
      .then(() => {
        refreshUser();
      });

    if (isUserNotConfident) {
      wizard.nextStep();
    } else {
      wizard.goToStep('setting_up_profile');
    }
  };

  const handleBackStep = () => {
    wizard.goToStep('name');
  };

  return (
    <OnboardingPage>
      <OnboardingNavigation onBack={handleBackStep} />

      <OnboardingTitle className="title">
        Do you struggle with self-confidence?
      </OnboardingTitle>

      <IonContent>
        <OnboardingOptionsList>
          {SELF_CONFIDENCE_OPTIONS.map((option) => (
            <OnboardingOptionItem
              checked={selfConfidence === option.id}
              control={form.control}
              option={option}
              name="self_confidence"
              key={option.id}
              onChange={(value) => {
                queryParamsModule.setParam<LayoutDataKeyType>(
                  'self_confidence',
                  value,
                );
                handleNextStep();
              }}
            />
          ))}
        </OnboardingOptionsList>
      </IonContent>
    </OnboardingPage>
  );
};
